import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../FreeAssesment/loader.css";
import logo from "../../assets/ExamWarriorLogo.svg";

function PracticeTestPage() {
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});
    const [showPopup, setShowPopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const isResizing = useRef(false);
    const startX = useRef(0);

    const user = JSON.parse(localStorage.getItem('user'));
    const childId = user?.id || "";

    // Enhanced option parser for frontend
    const parseMcqOptions = (optionsString) => {
        if (!optionsString) return [];
        
        // Split options by comma but preserve the full text
        const options = optionsString.split(/\s*,\s*/);
        
        return options.map((opt, index) => {
            // Extract letter (A-E) if present
            const letterMatch = opt.match(/^([A-E])[\.\s]*/);
            const letter = letterMatch ? letterMatch[1] : String.fromCharCode(65 + index);
            const text = letterMatch ? opt.replace(/^[A-E][\.\s]*/, '').trim() : opt.trim();
            
            return {
                letter,
                text,
                full: opt.trim(), // Original text for display
                value: letter // What we'll store as selected answer
            };
        });
    };

    // Handle MCQ answer selection
    const handleSelectAnswer = (questionId, option, category) => {
        setAnswers(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [questionId]: option.value // Store just the letter (A, B, C)
            }
        }));
    };

    // Handle writing answer input
    const handleWritingAnswer = (event) => {
        const inputText = event.target.value;
        const wordCount = inputText.trim().split(/\s+/).length;

        if (wordCount > 300) {
            toast.error("Your response is too long. Please limit to 300 words.");
            return;
        }

        setAnswers((prev) => ({
            ...prev,
            writing: inputText
        }));
    };

    // Submit exam handler
    const handleSubmit = async () => {
        setIsLoading(true);

        if (!questions.length) {
            toast.error("No questions to submit.");
            return;
        }

        try {
            const categories = ["reading", "maths", "thinkingskills", "writing"];
            const sections = [];

            for (const category of categories) {
                const categoryQuestions = questions.filter(q => q.category === category);

                if (categoryQuestions.length === 0) continue;

                let responses = [];
                if (category === "writing") {
                    const writingResponse = answers.writing?.trim() || "";
                    if (!writingResponse.trim()) {
                        toast.error("Writing response is empty.");
                        continue;
                    }

                    responses.push({
                        questionId: categoryQuestions[0]?.id || 1,
                        writingResponse: writingResponse
                    });
                } else {
                    responses = categoryQuestions.map(q => ({
                        questionId: q.id,
                        selectedAnswer: answers[category]?.[q.id] || ""
                    }));
                }

                sections.push({
                    category,
                    questionStatus: categoryQuestions.map(q => ({
                        questionId: q.id,
                        status: answers[category]?.[q.id] ? "attempted" : "unattempted"
                    })),
                    responses
                });
            }

            const payload = {
                childId,
                testType: "Practice",
                sections
            };

            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/practice-test/submitPracticeTest`, payload, {
                headers: { "Content-Type": "application/json" }
            });

            navigate("/premium-tests");
        } catch (error) {
            toast.error("Error submitting test.");
            console.error("Submission Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        window.history.pushState(null, null, window.location.pathname);
        const preventBackNavigation = () => {
            window.history.pushState(null, null, window.location.pathname);
            alert("Back navigation is disabled during the test.");
        };

        window.addEventListener("popstate", preventBackNavigation);
        return () => window.removeEventListener("popstate", preventBackNavigation);
    }, []);

    const handleButtonClick = () => {
        setShowPopup(true);
    };

    const handleCancel = () => {
        setShowPopup(false);
    };

    const handleConfirm = () => {
        setShowPopup(false);
        handleSubmit();
    };

    useEffect(() => {
        const fetchPracticeTest = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/practice-test/getTestQuestions`);

                if (!response.data) {
                    throw new Error("Invalid API response structure.");
                }

                let subjects = [
                    { category: "reading", questions: response.data.reading || [] },
                    { category: "maths", questions: response.data.maths || [] },
                    { category: "thinkingskills", questions: response.data.thinkingskills || [] },
                ];

                // Shuffle subjects (except writing which should be last)
                subjects = subjects.sort(() => Math.random() - 0.5);
                subjects.push({ category: "writing", questions: [response.data.writing] });

                const flattenedQuestions = subjects.flatMap((subject) =>
                    subject.questions.map((q) => ({ ...q, category: subject.category }))
                );

                setQuestions(flattenedQuestions);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching practice test questions:", error);
                setLoading(false);
                toast.error("Failed to load practice test.");
            }
        };

        fetchPracticeTest();
    }, []);

    const handleMouseDown = (e) => {
        isResizing.current = true;
        startX.current = e.clientX;
        document.body.style.cursor = "col-resize";
    };

    if (loading || questions.length === 0) {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-yellow-50">
                <div className="loadingspinner">
                    <div id="square1" className="bg-orange-500"></div>
                    <div id="square2" className="bg-orange-400"></div>
                    <div id="square3" className="bg-orange-300"></div>
                    <div id="square4" className="bg-yellow-500"></div>
                    <div id="square5" className="bg-yellow-400"></div>
                </div>
            </div>
        );
    }

    const totalQuestions = questions.length;
    const currentQuestion = questions[currentIndex];

    return (
        <div className="h-screen bg-white flex flex-col">
            {/* Header */}
            <div className="absolute w-full h-24 flex justify-between items-center py-1 px-3 bg-white shadow-md">
                <img src={logo} alt="logo" className="h-24 w-32" />
                <div className="text-lg font-semibold">
                    Practice Test
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-grow flex flex-col md:flex-row pt-20 overflow-hidden">
                {/* Question Panel */}
                <div className="w-full md:w-1/2 bg-white p-6 overflow-y-auto max-h-[calc(100vh-120px)]">
                    <p className="text-2xl font-bold text-gray-800 mb-4">
                        <span>Question {currentIndex + 1}</span>
                        <br />
                        {currentQuestion?.question || "No question available"}
                    </p>

                    {currentQuestion?.extract_text && (
                        <p className="text-black mt-2">
                            <strong>Extract:</strong> {currentQuestion.extract_text}
                        </p>
                    )}

                    {currentQuestion?.image_data && (
                        <div className="mt-4">
                            <img
                                src={
                                    currentQuestion.image_data.startsWith("http")
                                        ? currentQuestion.image_data
                                        : `http://${currentQuestion.image_data}`
                                }
                                alt="Question Image"
                                className="max-w-full rounded-md shadow-lg"
                            />
                        </div>
                    )}
                </div>

                <div
                    className="resize-handle hidden md:block"
                    onMouseDown={handleMouseDown}
                    style={{
                        cursor: "col-resize",
                        backgroundColor: "gray",
                        width: "3px",
                        height: "auto",
                    }}
                ></div>

                {/* Answer Panel */}
                <div className="w-full md:w-1/2 bg-white pb-32 md:p-6">
                    <div className="space-y-6 mt-10">
                        {currentQuestion.category === "writing" ? (
                            <div>
                                <textarea
                                    className="w-full p-2 border rounded"
                                    rows="5"
                                    placeholder="Write your answer here..."
                                    onChange={handleWritingAnswer}
                                ></textarea>
                                <p className="text-gray-600 mt-2">
                                    Word Count: {answers.writing ? answers.writing.trim().split(/\s+/).length : 0} / 300
                                </p>
                            </div>
                        ) : (
                            parseMcqOptions(currentQuestion?.mcq_options).map((option, index) => (
                                <label key={index} className="flex items-center space-x-2 py-4 px-2 bg-gray-200 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors">
                                    <input
                                        type="radio"
                                        name={`question-${currentQuestion.id}`}
                                        value={option.value}
                                        checked={answers[currentQuestion.category]?.[currentQuestion.id] === option.value}
                                        onChange={() => handleSelectAnswer(currentQuestion.id, option, currentQuestion.category)}
                                        className="peer h-8 w-8"
                                    />
                                    <span className="text-black text-lg font-semibold">
                                        {option.full} {/* Show full option text like "A 975 g" */}
                                    </span>
                                </label>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Navigation Footer */}
            <div className="relative bottom-0 left-0 right-0 bg-orange-500 text-white py-3 flex justify-between">
                <button
                    onClick={() => setCurrentIndex((prev) => Math.max(prev - 1, 0))}
                    className={`p-2 px-4 rounded-md font-semibold bg-black ml-2 ${currentIndex === 0 ? "opacity-50" : ""}`}
                    disabled={currentIndex === 0}
                >
                    &lt;&lt; PREVIOUS
                </button>

                {currentIndex < totalQuestions - 1 ? (
                    <button
                        onClick={() => setCurrentIndex((prev) => prev + 1)}
                        className="p-2 px-4 rounded-md font-semibold bg-black mr-3"
                    >
                        NEXT &gt;&gt;
                    </button>
                ) : (
                    <button
                        onClick={handleButtonClick}
                        className="bg-black text-white font-bold p-2 px-6 mr-3 rounded-md"
                    >
                        Submit Practice Test
                    </button>
                )}
            </div>

            {/* Submission Confirmation Popup */}
            {showPopup && (
                <div className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="relative bg-white rounded-lg shadow-lg overflow-hidden transform transition-all w-full max-w-md sm:mx-auto">
                        <button
                            onClick={handleCancel}
                            type="button"
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="px-6 py-8">
                            <div className="flex items-center space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                        <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Confirm Submission
                                </h3>
                            </div>

                            <div className="mt-4 text-gray-700">
                                Are you sure you want to submit the practice test?
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    onClick={handleCancel}
                                    type="button"
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    type="button"
                                    className="px-4 py-2 text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Loading Indicator */}
            {isLoading && (
                <div className="fixed inset-0 flex justify-center items-center bg-gray-500 bg-opacity-50 z-50">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
                    <span className="ml-4 text-white">
                        Submitting your test and sending report...
                    </span>
                </div>
            )}

            <ToastContainer />
        </div>
    );
}

export default PracticeTestPage;